import { Suspense, lazy, useState, type FormEvent } from 'react';
import { isValidBarcode, normalizeBarcode } from '@almox/shared';

// A camera so e carregada quando alguem pede para escanear: a biblioteca de
// decodificacao pesa mais que o resto do app somado.
const BarcodeScanner = lazy(() => import('./BarcodeScanner.js'));

interface Props {
  /** `null` significa "este produto nao tem codigo de barras". */
  onResolve: (barcode: string | null) => void;
  onCancel: () => void;
  title?: string;
  hint?: string;
}

/**
 * Passo do codigo de barras, pedido ANTES da leitura da embalagem por foto.
 *
 * A ordem importa: o codigo e o que identifica o produto na proxima compra —
 * fotografar primeiro e perguntar depois deixava o cadastro pronto e o vinculo
 * para tras. Com o codigo em maos, o Almox ainda descobre de graca que o
 * produto ja esta no catalogo e nem gasta uma leitura de IA.
 */
export function BarcodeGate({ onResolve, onCancel, title, hint }: Props) {
  const [codigo, setCodigo] = useState('');
  const [camera, setCamera] = useState(false);

  const limpo = normalizeBarcode(codigo);
  const suspeito = limpo.length > 0 && !isValidBarcode(limpo);

  function confirmar(evento: FormEvent) {
    evento.preventDefault();
    if (!limpo) return;
    onResolve(limpo);
  }

  return (
    <div className="card card-gate">
      <h3 style={{ margin: '0 0 4px' }}>{title ?? 'Qual e o codigo de barras?'}</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        {hint ??
          'Escaneie ou digite o codigo antes da foto: e ele que reconhece o produto na proxima compra.'}
      </p>

      {camera ? (
        <Suspense fallback={<p className="small muted">Abrindo a camera…</p>}>
          <BarcodeScanner
            onDetect={(lido) => {
              setCamera(false);
              onResolve(normalizeBarcode(lido));
            }}
            onCancel={() => setCamera(false)}
          />
        </Suspense>
      ) : (
        <button type="button" className="btn btn-primary btn-block" onClick={() => setCamera(true)}>
          📷 Escanear o codigo com a camera
        </button>
      )}

      <form onSubmit={confirmar} style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="gate-barcode">Ou digite o codigo</label>
          <input
            id="gate-barcode"
            value={codigo}
            onChange={(evento) => setCodigo(evento.target.value)}
            placeholder="7891000100103"
            inputMode="numeric"
          />
          {suspeito ? (
            <span className="small" style={{ color: 'var(--warn)' }}>
              Digito verificador nao bate — confira, mas da para seguir assim mesmo.
            </span>
          ) : null}
        </div>
        <button type="submit" className="btn btn-block" disabled={!limpo}>
          Usar este codigo
        </button>
      </form>

      <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onResolve(null)}>
          Nao tem codigo de barras
        </button>
      </div>
    </div>
  );
}
